/**
 * ValeraneCart : API unique du panier (tiroir) avec affichage optimiste.
 *
 * Principe : au clic, le tiroir s'ouvre et la ligne d'article s'affiche tout de suite (donnees produit deja
 * connues dans la page), puis les requetes partent dans une file serialisee vers Shopify avec la Section
 * Rendering API (parametre `sections`) : un seul aller-retour par action. Quand la file est vide, le HTML du
 * serveur remplace l'affichage optimiste (reconciliation par les ecouteurs existants de CartLinesUpdateEvent).
 * En cas d'erreur, retour a l'etat reel du serveur + message clair.
 *
 * ---------------------------------------------------------------------------------------------------------
 * API (window.ValeraneCart)
 *
 *   ValeraneCart.add({
 *     id,                 // number|string  identifiant de variante (obligatoire, sauf `items` ou `formData`)
 *     quantity,           // number         defaut 1
 *     properties,         // object         proprietes de ligne (optionnel)
 *     selling_plan,       // number|string  (optionnel)
 *     preview: {          // donnees d'affichage immediat (optionnel : sinon le cache produit est utilise)
 *       title, variantTitle, image, price, compareAtPrice, url,   // price/compareAtPrice en centimes
 *       options: [{ name, value }],
 *     },
 *     items,              // Array<{id, quantity, properties?, preview?}>  ajout groupe (remplace id/quantity)
 *     formData,           // FormData       formulaire produit complet (cartes cadeaux, plans de vente...)
 *     source,             // Element        emetteur de l'evenement (defaut : document.body)
 *     context,            // string         contexte de l'evenement ('product' par defaut)
 *   }) -> Promise<{ ok: boolean, response?: object, message?: string, errors?: any }>   (ne rejette jamais)
 *
 *   ValeraneCart.change({ key | line, quantity, source? }) -> Promise<{ ok, response?, message? }>
 *   ValeraneCart.remove(keyOrLine)                           -> Promise  (quantite 0)
 *   ValeraneCart.open() / ValeraneCart.close()               -> ouvre / ferme le tiroir
 *   ValeraneCart.preload(productUrlOrHandle)                 -> Promise  (charge titre/variantes/images en cache)
 *   ValeraneCart.registerPreview(variantId, preview)         -> void    (fournir soi-meme les donnees d'une variante)
 *   ValeraneCart.hasPreview(variantId)                       -> boolean (le cache sait-il decrire cette variante ?)
 *   ValeraneCart.getCart()                                   -> Promise<cart.js>
 *   ValeraneCart.enqueue(job)                                -> Promise  (file serialisee, job({isLast}) )
 *   ValeraneCart.resolveKey(key) / renumber()                -> utilitaires internes (cles provisoires)
 *   ValeraneCart.deferSubmit(event)                          -> stoppe la propagation d'un « submit » puis le rejoue (voir ci-dessous)
 *   ValeraneCart.version
 * ---------------------------------------------------------------------------------------------------------
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { prefersReducedMotion } from '@theme/utilities';

const SECTION_FALLBACK = 'cart-drawer-section';
const SOURCE = 'valerane-cart';

/** @type {{ pending: number, queue: Promise<any>, count: number | null, total: number | null,
 *   keyMap: Map<string,string>, previews: Map<string, any>, products: Map<string, Promise<any>>, seq: number,
 *   controller: AbortController }} */
const state = {
  pending: 0,
  queue: Promise.resolve(),
  count: null,
  total: null,
  keyMap: new Map(),
  previews: new Map(),
  products: new Map(),
  seq: 0,
  controller: new AbortController(),
  skActive: false,
  changeSeq: new Map(),
};

/* ------------------------------------------------------------------ utilitaires */

const drawerEl = () => /** @type {any} */ (document.getElementById('cart-drawer'));
const cartIcon = () => /** @type {any} */ (document.querySelector('cart-icon'));
const routes = () => /** @type {any} */ (window).Theme?.routes ?? {};
const idle = (/** @type {() => void} */ fn) => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 200));

/**
 * Change le texte d'un element SANS inserer/retirer de noeud quand il contient deja un noeud texte :
 * toute insertion dans le document force Chrome a recalculer le style de la page entiere (selecteurs :has() du theme).
 * @param {Element} el
 * @param {string} text
 */
function setText(el, text) {
  const n = el.firstChild;
  if (n && n.nodeType === 3 && !n.nextSibling) {
    if (n.nodeValue !== text) n.nodeValue = text;
  } else if (el.textContent !== text) {
    el.textContent = text;
  }
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  if (ids.size === 0 && drawerEl()) ids.add(SECTION_FALLBACK);
  return [...ids];
}

/** @param {number} cents */
function money(cents, kind = 'items') {
  const tpl = /** @type {HTMLTemplateElement | null} */ (document.getElementById('valerane-cart-row-template'));
  if (!tpl) return String(cents / 100);
  const format = kind === 'total' ? tpl.dataset.moneyFormatTotal || tpl.dataset.moneyFormat : tpl.dataset.moneyFormat;
  return formatMoney(cents, format || '{{amount}}', tpl.dataset.currency || 'EUR');
}

/** @param {string} url @param {number} width */
function withWidth(url, width) {
  if (!url) return '';
  try {
    const u = new URL(url, location.href);
    // CDN Shopify : domaine cdn.shopify.com ou chemin /cdn/shop/ du domaine de la boutique (le cas ici).
    if (/cdn\.shopify\.com/.test(u.host) || /^\/cdn\/shop\//.test(u.pathname)) u.searchParams.set('width', String(width));
    return u.toString();
  } catch (_) {
    return url;
  }
}

/** Resout une cle provisoire (ligne optimiste) vers la vraie cle Shopify, si elle est deja connue. */
function resolveKey(key) {
  return state.keyMap.get(key) ?? key;
}

/* ------------------------------------------------------------------ cache produit (donnees d'apercu) */

/** @param {string} urlOrHandle */
function productJsUrl(urlOrHandle) {
  let path = urlOrHandle.startsWith('/') || urlOrHandle.startsWith('http') ? urlOrHandle : `/products/${urlOrHandle}`;
  try {
    const u = new URL(path, location.origin);
    path = u.pathname;
  } catch (_) {
    /* garde path */
  }
  return path.replace(/\.js$/, '') + '.js';
}

/** @param {string} urlOrHandle */
function preload(urlOrHandle) {
  if (!urlOrHandle) return Promise.resolve();
  const url = productJsUrl(urlOrHandle);
  if (state.products.has(url)) return /** @type {Promise<any>} */ (state.products.get(url));
  const p = fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((product) => {
      if (!product) return;
      // Miniatures du tiroir prechargees (meme URL que l'apercu) : l'image est deja la quand la ligne apparait.
      new Set((product.variants || []).map((v) => v.featured_image?.src).concat(product.featured_image || product.images?.[0])).forEach((src) => {
        if (src) new Image().src = withWidth(src, 250);
      });
      const names = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
      for (const v of product.variants || []) {
        state.previews.set(String(v.id), {
          productId: product.id,
          title: product.title,
          variantTitle: v.title,
          options: names.map((name, i) => ({ name, value: v.options?.[i] ?? v[`option${i + 1}`] })),
          hasOnlyDefaultVariant: product.variants.length === 1 && v.title === 'Default Title',
          image: v.featured_image?.src || product.featured_image || product.images?.[0],
          price: v.price,
          compareAtPrice: v.compare_at_price,
          url: product.url || `/products/${product.handle}`,
        });
      }
    })
    .catch(() => state.products.delete(url));
  state.products.set(url, p);
  return p;
}

/** @param {string|number} variantId @param {any} preview */
function registerPreview(variantId, preview) {
  state.previews.set(String(variantId), { ...(state.previews.get(String(variantId)) || {}), ...preview });
}

/** Donnees d'apercu : cache + fournies par l'appelant + repli sur la carte produit. */
function previewFor(item, source) {
  const merged = { ...(state.previews.get(String(item.id)) || {}), ...(item.preview || {}) };
  if ((!merged.title || !merged.image) && source instanceof Element) {
    const card = source.closest('product-card, .product-card, quick-add-component');
    if (card) {
      merged.title ||= card.querySelector('[class*="title"], h2, h3')?.textContent?.trim();
      const img = card.querySelector('img');
      merged.image ||= img?.currentSrc || img?.getAttribute('src');
      merged.url ||= card.querySelector('a[href*="/products/"]')?.getAttribute('href');
    }
  }
  return merged;
}

/* ------------------------------------------------------------------ affichage optimiste */

/** Arbre actif du tiroir : le squelette « rempli » tant qu'il est affiche, sinon le contenu serveur. */
function root() {
  const d = drawerEl();
  if (!d) return null;
  return (state.skActive && d.querySelector('[data-vc-skeleton]')) || d;
}

function rows() {
  return /** @type {HTMLElement[]} */ ([...(root()?.querySelectorAll('.cart-items__table-row') ?? [])]);
}

/** Renumerote les lignes (index utilises par les actions on:click et les refs d'erreur). */
function renumber() {
  rows().forEach((row, i) => {
    const n = i + 1;
    const input = row.querySelector('input[data-cart-line]');
    if (input) input.setAttribute('data-cart-line', String(n));
    row.querySelectorAll('button.cart-items__remove').forEach((b) => b.setAttribute('on:click', `/onLineItemRemove/${n}`));
    const errCell = row.querySelector('.cart-items__error');
    if (errCell) errCell.setAttribute('ref', `cartItemErrorContainer-${n}`);
    const errText = row.querySelector('.cart-item__error-text');
    if (errText) errText.setAttribute('ref', `cartItemError-${n}`);
  });
}

function openDrawer() {
  window.__loadCartDrawer?.();
  const d = drawerEl();
  if (d && typeof d.open === 'function' && !d.hasAttribute('open')) d.open();
}

function closeDrawer() {
  const d = drawerEl();
  if (d && typeof d.close === 'function') d.close();
}

const skeletonEl = () => /** @type {HTMLElement | null} */ (drawerEl()?.querySelector('[data-vc-skeleton]') ?? null);
const serverInner = () => /** @type {HTMLElement | null} */ (drawerEl()?.querySelector('.cart-drawer__inner[data-hydration-key]') ?? null);

/** Construit (a l'idle) le squelette « rempli » cache, tant que le panier serveur est vide. */
function warmSkeleton() {
  const sk = skeletonEl();
  const inner = serverInner();
  const tpl = /** @type {HTMLTemplateElement | null} */ (document.getElementById('valerane-cart-filled-template'));
  if (!sk || !inner || !tpl || state.skActive || sk.firstElementChild) return;
  if (!inner.classList.contains('cart-drawer--empty')) return;
  sk.replaceChildren(document.importNode(tpl.content, true));
}

/** Premier ajout : bascule (attribut hidden) vers le squelette « rempli » deja construit. */
function fillSkeleton() {
  const sk = skeletonEl();
  const inner = serverInner();
  if (!sk || !inner) return false;
  if (!sk.firstElementChild) {
    const tpl = /** @type {HTMLTemplateElement | null} */ (document.getElementById('valerane-cart-filled-template'));
    if (!tpl) return false;
    sk.replaceChildren(document.importNode(tpl.content, true));
  }
  state.skActive = true;
  sk.hidden = false;
  inner.hidden = true;
  return true;
}

/** Retour au contenu serveur (reconcilie ou panier restate vide) : le squelette est vide et recache. */
/** Blocs deja affiches dans le squelette (idees d'achat, reduction) : on garde ces memes noeuds a la place de ceux du serveur. */
/**
 * Miniatures des lignes : l'image du squelette (deja chargee et affichee) remplace celle du serveur quand c'est le meme
 * fichier a la meme taille. Sinon le nouvel element repartait de zero (vide le temps du chargement) : c'etait le
 * « rafraichissement » de l'image quelques secondes apres l'ajout.
 */
function adoptRowImages(sk, inner) {
  const abs = (/** @type {string | null} */ u) => (u ? new URL(u, location.href).href : '');
  sk.querySelectorAll('.cart-items__table-row[data-key]:not(.vc-spare)').forEach((row) => {
    const mine = /** @type {HTMLImageElement | null} */ (row.querySelector('img[data-vc="image"]'));
    const key = resolveKey(/** @type {HTMLElement} */ (row).dataset.key || '');
    const theirRow = [...inner.querySelectorAll('.cart-items__table-row[data-key]')].find((r) => /** @type {HTMLElement} */ (r).dataset.key === key);
    const theirs = /** @type {HTMLImageElement | null} */ (theirRow?.querySelector('img.cart-items__media-image') ?? null);
    if (!mine || !theirs || !mine.complete || abs(mine.getAttribute('src')) !== abs(theirs.getAttribute('src'))) return;
    mine.alt = theirs.alt;
    mine.removeAttribute('data-vc');
    theirs.replaceWith(mine);
  });
}

const KEPT_BLOCKS = ['cart-cross-sell-component', '.cart-drawer-discount'];

/**
 * Au retour au contenu serveur, les blocs conserves du squelette remplacent leur version serveur AVANT que le
 * serveur ne soit reaffiche (il est encore cache) : meme balisage, meme etat ouvert/ferme, aucun clignotement.
 */
function adoptSkeletonBlocks(sk, inner) {
  if (!sk || !inner || inner.classList.contains('cart-drawer--empty')) return;
  adoptRowImages(sk, inner);
  for (const sel of KEPT_BLOCKS) {
    const mine = sk.querySelector(sel);
    const theirs = inner.querySelector(sel);
    if (!mine || !theirs) continue;
    if (mine instanceof HTMLElement && theirs instanceof HTMLElement && theirs.dataset.cartProductIds != null) {
      mine.dataset.cartProductIds = theirs.dataset.cartProductIds;
      const ids = new Set(theirs.dataset.cartProductIds.split(',').filter(Boolean));
      mine.querySelectorAll('.cart-cross-sell__card').forEach((c) => {
        if (ids.has(/** @type {HTMLElement} */ (c).dataset.productId || '')) c.remove();
      });
    }
    theirs.replaceWith(mine);
  }
}

function endSkeleton() {
  if (!state.skActive) return;
  state.skActive = false;
  const sk = skeletonEl();
  const inner = serverInner();
  adoptSkeletonBlocks(sk, inner);
  if (inner) inner.hidden = false;
  if (sk) {
    sk.hidden = true;
    sk.replaceChildren();
  }
  idle(warmSkeleton);
}

/** Appele apres chaque mutation du tiroir : le squelette cede la place quand le serveur a rendu le panier rempli. */
function checkSkeleton() {
  if (!state.skActive) return;
  const inner = serverInner();
  if (inner && !inner.classList.contains('cart-drawer--empty')) return endSkeleton();
  setTimeout(() => {
    if (state.skActive && state.pending === 0 && state.count === 0) endSkeleton();
  }, 600);
}

/** Remplace, dans le tiroir, l'ancien montant formate par le nouveau (sous-total, bouton de paiement...). */
function replaceMoneyText(oldText, newText) {
  const drawer = drawerEl();
  if (!drawer || !oldText || oldText === newText) return;
  const walker = document.createTreeWalker(drawer.querySelector('.cart-drawer__summary') ?? drawer, NodeFilter.SHOW_TEXT);
  /** @type {Text[]} */ const hits = [];
  while (walker.nextNode()) {
    const n = /** @type {Text} */ (walker.currentNode);
    if (n.nodeValue?.includes(oldText)) hits.push(n);
  }
  hits.forEach((n) => (n.nodeValue = n.nodeValue.split(oldText).join(newText)));
  drawer.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
    if (el.getAttribute('value') === oldText) el.setAttribute('value', newText);
  });
}

/** @param {number} deltaCents */
function bumpTotal(deltaCents) {
  if (state.total == null || !deltaCents) return;
  const before = money(state.total, 'total');
  state.total = Math.max(0, state.total + deltaCents);
  replaceMoneyText(before, money(state.total, 'total'));
  updateFreeShipping(state.total);
}

function updateFreeShipping(totalCents) {
  if (totalCents == null) return;
  const banner = document.querySelector('.cart-free-shipping-banner.free-shipping, .free-shipping');
  if (!banner) return;
  const threshold = parseInt(banner.dataset.threshold || '7000', 10);
  const progressBar = banner.querySelector('.free-shipping__progress-bar');
  const progressTrack = banner.querySelector('.free-shipping__progress');
  const remainingEl = banner.querySelector('[data-free-shipping-remaining]');
  const textEl = banner.querySelector('.free-shipping__text');
  const pct = Math.min(100, Math.max(0, Math.round((totalCents / threshold) * 1000) / 10));
  if (progressBar) progressBar.style.width = `${pct}%`;
  if (progressTrack) progressTrack.setAttribute('aria-valuenow', String(pct));
  if (totalCents >= threshold) {
    if (textEl) textEl.innerHTML = '<span>Félicitations ! La <strong>livraison est offerte</strong>.</span>';
  } else {
    const left = threshold - totalCents;
    if (remainingEl) {
      remainingEl.textContent = money(left, 'price');
    } else if (textEl) {
      textEl.innerHTML = `<span>Plus que <strong data-free-shipping-remaining>${money(left, 'price')}</strong> pour bénéficier de la <strong>livraison offerte</strong></span>`;
    }
  }
}

/**
 * Banniere de reduction par paliers (snippets/cart-tiered-offer-banner.liquid) : meme message, meme jauge, memes
 * jalons que le rendu serveur pour le nombre d'articles donne, afin que le HTML serveur arrivant ensuite soit identique.
 * @param {number} count
 */
function updateTieredOffer(count) {
  const root = drawerEl();
  const banner = root?.querySelector('.cart-drawer__inner:not([hidden]) cart-tiered-offer');
  if (!banner) return;
  const steps = [
    [0, 0, 'Ajoutez <strong>2 articles</strong> et obtenez <strong>10% de réduction</strong> !'],
    [16.66, 1, 'Ajoutez <strong>1 article</strong> et obtenez <strong>10% de réduction</strong> !'],
    [33.33, 2, 'Ajoutez <strong>1 article</strong> et obtenez <strong>15% de réduction</strong> !'],
    [66.66, 3, 'Ajoutez <strong>1 article</strong> et obtenez <strong>20% de réduction</strong> !'],
    [100, 4, '🎉 <strong>Félicitations !</strong> Vous profitez de <strong>20% de réduction</strong> !'],
  ];
  const [pct, , message] = steps[Math.min(count, 4)];
  const msg = banner.querySelector('.cto-message');
  if (msg && msg.innerHTML !== message) msg.innerHTML = message;
  const fill = /** @type {HTMLElement | null} */ (banner.querySelector('.cto-fill'));
  if (fill) fill.style.width = `${pct}%`;
  [2, 3, 4].forEach((need, i) => {
    const node = banner.querySelector(`.cto-node--${i + 1}`);
    if (!node) return;
    node.classList.toggle('is-unlocked', count >= need);
    node.classList.toggle('is-next', count < need && (count === need - 1 || (i === 0 && count === 0)));
    node.classList.toggle('is-locked', count < need && !(count === need - 1 || (i === 0 && count === 0)));
  });
}

/** @param {number} deltaCount */
function bumpCount(deltaCount) {
  const icon = cartIcon();
  if (!icon?.renderCartBubble) return;
  const current = state.count ?? icon.currentCartCount ?? 0;
  state.count = Math.max(0, current + deltaCount);
  icon.renderCartBubble(state.count, deltaCount > 0);
  updateTieredOffer(state.count);
}

/** Met a jour prix de ligne et quantite d'une ligne deja affichee. */
function setRowQuantity(row, qty, unitCents) {
  const input = /** @type {HTMLInputElement | null} */ (row.querySelector('input[data-cart-line], input[name="updates[]"]'));
  if (input) {
    input.value = String(qty);
    input.defaultValue = String(qty);
    input.setAttribute('data-cart-quantity', String(qty));
  }
  row.dataset.vcQty = String(qty);
  const unit = unitCents ?? Number(row.dataset.unitPrice || 0);
  const price = row.querySelector('.cart-items__price-current');
  if (price && unit) {
    const text = money(unit * qty);
    price.setAttribute('value', text);
    setText(price, text);
    const compareUnit = Number(row.dataset.compareUnit || 0);
    const compare = row.querySelector('.cart-item__price-compare');
    if (compare && compareUnit) setText(compare, money(compareUnit * qty));
    const savings = row.querySelector('.cart-item__savings-amount');
    if (savings && compareUnit > unit) setText(savings, `(-${money((compareUnit - unit) * qty)})`);
  }
  row.querySelectorAll('cart-quantity-selector-component').forEach((sel) => /** @type {any} */ (sel).updateButtonStates?.());
}

const SLOTS = 3;

/**
 * Ligne "vierge" tiree du modele : emplacements de variantes et de prix barre deja crees (masques) pour que le
 * remplissage au clic ne fasse que changer du texte et des attributs, sans aucune insertion dans le document.
 * @returns {HTMLElement | null}
 */
function newRow() {
  const tpl = /** @type {HTMLTemplateElement | null} */ (document.getElementById('valerane-cart-row-template'));
  const row = /** @type {HTMLElement | null} */ (tpl?.content.firstElementChild?.cloneNode(true) ?? null);
  if (!row) return null;
  const title = row.querySelector('[data-vc="title"]');
  if (title && !title.firstChild) title.append(document.createTextNode(' '));
  const price = row.querySelector('.cart-items__price-current');
  if (price && !price.firstChild) price.append(document.createTextNode(' '));
  const dl = row.querySelector('[data-vc="variants"]');
  for (let i = 0; dl && i < SLOTS; i++) {
    const wrap = document.createElement('div');
    wrap.className = 'cart-items__variant';
    wrap.style.display = 'none';
    const dt = document.createElement('dt');
    dt.className = 'visually-hidden';
    dt.append(document.createTextNode(' '));
    const dd = document.createElement('dd');
    dd.append(document.createTextNode(' '));
    wrap.append(dt, dd);
    dl.append(wrap);
  }
  const s = document.createElement('s');
  s.className = 'cart-item__price-compare';
  s.style.display = 'none';
  s.append(document.createTextNode(' '));
  row.querySelector('.cart-items__price-numbers')?.append(s);
  const sv = document.createElement('span');
  sv.className = 'cart-item__savings-amount cart-drawer__savings-text';
  sv.style.display = 'none';
  sv.append(document.createTextNode(' '));
  row.querySelector('.cart-items__price-inner')?.append(sv);
  return row;
}

/** Ligne de rechange masquee, deja inseree dans le tableau (connectee) pendant les temps morts. */
function warmSpare() {
  const tbody = drawerEl()?.querySelector('.cart-items__table tbody');
  if (!tbody || tbody.querySelector(':scope > tr.vc-spare')) return;
  const row = newRow();
  if (!row) return;
  row.dataset.vcClass = row.className;
  row.className = 'vc-spare';
  row.removeAttribute('ref');
  row.style.display = 'none';
  tbody.prepend(row);
}

/** @returns {HTMLElement | null} */
function takeSpare() {
  const row = /** @type {HTMLElement | null} */ (drawerEl()?.querySelector('.cart-items__table tbody > tr.vc-spare') ?? null);
  if (!row) return null;
  row.className = row.dataset.vcClass || 'cart-items__table-row';
  delete row.dataset.vcClass;
  row.setAttribute('ref', 'cartItemRows[]');
  row.style.display = '';
  return row;
}

/** Retire des idees d'achat du squelette l'article qui vient d'etre ajoute (le serveur ne le propose pas non plus). */
function pruneCrossSell(productId) {
  const comp = skeletonEl()?.querySelector('cart-cross-sell-component');
  if (!(comp instanceof HTMLElement)) return;
  const id = String(productId);
  comp.querySelectorAll(`.cart-cross-sell__card[data-product-id="${id}"]`).forEach((c) => c.remove());
  const ids = (comp.dataset.cartProductIds || '').split(',').filter(Boolean);
  if (!ids.includes(id)) comp.dataset.cartProductIds = [...ids, id].join(',');
}

/** @returns {HTMLElement | null} */
function buildRow(item, p, key) {
  const row = takeSpare() ?? newRow();
  if (!row) return null;
  row.dataset.key = key;
  row.id = `CartItem-${key}`;
  const url = p.url || '#';
  if (p.productId) pruneCrossSell(p.productId);
  row.querySelectorAll('a[data-vc]').forEach((a) => a.setAttribute('href', url));
  const title = row.querySelector('[data-vc="title"]');
  if (title) setText(title, p.title || '');
  const img = /** @type {HTMLImageElement | null} */ (row.querySelector('[data-vc="image"]'));
  if (img && p.image) {
    // Memes attributs que le rendu serveur (image_url: width: 250 | image_tag) : meme URL, meme srcset, meme hauteur.
    const src = withWidth(p.image, 250);
    img.src = src;
    img.srcset = `${src} 250w`;
    img.setAttribute('height', '250');
    img.alt = p.title || '';
  }
  const dl = /** @type {HTMLElement | null} */ (row.querySelector('[data-vc="variants"]'));
  const opts = (p.options || []).filter((o) => o?.value);
  if (dl) {
    const noVariant = p.hasOnlyDefaultVariant || (opts.length === 0 && !p.variantTitle);
    dl.style.display = noVariant ? 'none' : '';
    const list = noVariant ? [] : opts.length ? opts : [{ name: '', value: p.variantTitle }];
    [...dl.children].slice(0, SLOTS).forEach((wrap, i) => {
      const w = /** @type {HTMLElement} */ (wrap);
      w.style.display = i < list.length ? '' : 'none';
      if (i >= list.length) return;
      const o = list[i];
      setText(w.querySelector('dt'), o.name ? `${o.name}:` : '');
      setText(w.querySelector('dd'), o.value + (i < list.length - 1 ? ', ' : ''));
    });
  }
  row.querySelector('cart-quantity-selector-component')?.setAttribute('data-variant-id', String(item.id));
  row.dataset.variantId = String(item.id);
  const unit = Number(p.price) || 0;
  const compare = Number(p.compareAtPrice) || 0;
  const onSale = compare > unit;
  row.dataset.unitPrice = String(unit);
  row.dataset.compareUnit = String(onSale ? compare : 0);
  row.querySelector('.cart-items__price-inner')?.classList.toggle('cart-items__price-inner--on-sale', onSale);
  row.querySelector('.cart-items__price-current')?.classList.toggle('cart-items__price-current--sale', onSale);
  row.querySelectorAll('.cart-item__price-compare, .cart-item__savings-amount').forEach((el) => {
    /** @type {HTMLElement} */ (el).style.display = onSale ? '' : 'none';
  });
  setRowQuantity(row, item.quantity, unit);
  return row;
}

/**
 * Ouvre tout de suite le tiroir (mode `fast-open` de theme-drawer.js : simple panneau non modal, sans showModal()).
 * Piege de focus, inert, verrou de defilement et evenement d'ouverture sont poses par le tiroir apres le premier rendu.
 * Respecte le reglage « ouvrir le panier a l'ajout » (attribut auto-open) et laisse l'evenement gerer les cas
 * particuliers (ajout depuis un dialogue modal, ex. ajout rapide).
 * @param {Element | undefined} source
 * @returns {boolean} vrai si le tiroir vient d'etre ouvert
 */
function openNow(source) {
  window.__loadCartDrawer?.();
  const d = /** @type {any} */ (drawerEl());
  if (!d || typeof d.open !== 'function' || d.hasAttribute('open') || !d.hasAttribute('fast-open')) return false;
  if (!d.querySelector('cart-drawer-component')?.hasAttribute('auto-open')) return false;
  if (source instanceof Element && source.closest('dialog:modal')) return false;
  d.open();
  return true;
}

/** Ajoute (ou incremente) les lignes dans le tiroir, ouvre le tiroir, met a jour pastille et total. */
function optimisticAdd(items, source) {
  const drawer = drawerEl();
  if (!drawer) return 0;
  const wasEmpty = !state.skActive && Boolean(serverInner()?.querySelector('[data-cart-drawer-empty]') || serverInner()?.classList.contains('cart-drawer--empty'));
  performance.mark('vc:optimistic:start');
  if (wasEmpty && !fillSkeleton()) return 0;
  performance.mark('vc:skeleton');
  // Le bouton de paiement du modele est rendu « panier vide » (desactive, gris) : on l'active tout de suite, comme le rendu serveur.
  if (wasEmpty) skeletonEl()?.querySelector('#checkout[disabled]')?.removeAttribute('disabled');

  const tbody = root()?.querySelector('.cart-items__table tbody');
  let deltaTotal = 0;
  let deltaCount = 0;
  let unknownPrice = false;
  // Panier serveur vide connu : total de depart 0 (sinon, total pas encore synchronise = « Paiement 0,00 € » fige).
  if (wasEmpty && state.total == null) state.total = 0;
  if (wasEmpty && state.count == null) state.count = 0;
  for (const item of items) {
    const p = previewFor(item, source);
    const unit = Number(p.price) || 0;
    if (!unit) unknownPrice = true;
    deltaCount += item.quantity;
    deltaTotal += unit * item.quantity;
    if (!tbody) continue;
    const hasProps = item.properties && Object.keys(item.properties).length > 0;
    const existing = hasProps
      ? null
      : rows().find((r) => !r.dataset.parentKey && r.querySelector(`[data-variant-id="${item.id}"]`) && !r.querySelector('.cart-items__properties'));
    if (existing) {
      const input = existing.querySelector('input[name="updates[]"]');
      const next = (Number(/** @type {HTMLInputElement | null} */ (input)?.value) || 0) + item.quantity;
      setRowQuantity(existing, next, unit || undefined);
      item.__row = existing;
      continue;
    }
    if (!p.title && !p.image) continue; // pas assez de donnees : le serveur remplira la ligne
    const key = `vc-pending-${++state.seq}`;
    const row = buildRow(item, p, key);
    if (!row) continue;
    if (!row.isConnected) tbody.prepend(row);
    item.__row = row;
    item.__pendingKey = key;
    if (!prefersReducedMotion()) {
      row.classList.add('adding');
      // pas de onAnimationEnd() ici : getAnimations() force un recalcul de style de toute la page
      row.addEventListener('animationend', () => row.classList.remove('adding'), { once: true });
    }
  }
  performance.mark('vc:rows');
  renumber();
  bumpTotal(deltaTotal);
  // Prix inconnu (carte sans donnees produit) : pas de faux montant, on masque le total jusqu'au rendu serveur.
  if (unknownPrice) root()?.querySelectorAll('.cart__checkout-button [ref="cartTotal"]').forEach((el) => /** @type {HTMLElement} */ (el).style.setProperty('display', 'none'));
  // Palier de reduction : calcule tout de suite (meme valeur que la pastille posee apres la glissiere).
  if (state.count != null) updateTieredOffer(state.count + deltaCount);
  performance.mark('vc:total');
  return deltaCount;
}

/* ------------------------------------------------------------------ erreurs */

let toastTimer = 0;
function showError(message) {
  const host = drawerEl()?.querySelector('dialog') ?? drawerEl();
  if (!host || !message) return;
  let toast = host.querySelector('.valerane-cart__toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'valerane-cart__toast';
    toast.setAttribute('role', 'alert');
    host.append(toast);
  }
  toast.textContent = message;
  toast.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (toast.hidden = true), 6000);
}

/* ------------------------------------------------------------------ reseau (file serialisee) */

/**
 * Ajoute une tache a la file : une seule requete panier a la fois, dans l'ordre des clics.
 * @template T
 * @param {(ctx: { isLast: () => boolean, signal: AbortSignal }) => Promise<T>} job
 * @returns {Promise<T>}
 */
function enqueue(job) {
  state.pending += 1;
  const ctx = { isLast: () => state.pending <= 1, signal: state.controller.signal };
  const run = state.queue.then(() => job(ctx));
  state.queue = run
    .catch(() => {})
    .then(() => {
      state.pending -= 1;
    });
  return run;
}

async function getCart() {
  const r = await fetch(`${routes().cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    signal: state.controller.signal,
  });
  if (!r.ok) throw new Error(`cart.js ${r.status}`);
  return r.json();
}

/** Synchronise compteur et total avec le serveur, en arriere-plan. */
function syncFromServer() {
  if (state.pending > 0) return Promise.resolve();
  return getCart()
    .then((cart) => {
      if (state.pending > 0) return;
      state.count = cart.item_count;
      state.total = cart.total_price;
      updateFreeShipping(state.total);
      cartIcon()?.renderCartBubble?.(cart.item_count, false);
      return cart;
    })
    .catch(() => {});
}

function parseItemCount(html) {
  if (!html) return null;
  const m = /ref="cartItemCount"[^>]*>\s*(\d+)\s*</.exec(html);
  return m ? parseInt(m[1], 10) : null;
}

function eventTarget(source) {
  return source instanceof Element ? source : document.body;
}

/* ------------------------------------------------------------------ API : add */

/** @param {any} opts */
function add(opts = {}) {
  window.__loadCartDrawer?.();
  /** @type {Array<any>} */
  let items;
  if (Array.isArray(opts.items) && opts.items.length) {
    items = opts.items.map((i) => ({ ...i, quantity: Number(i.quantity) || 1 }));
  } else {
    const fd = opts.formData instanceof FormData ? opts.formData : null;
    const id = opts.id ?? fd?.get('id');
    const quantity = Number(opts.quantity ?? fd?.get('quantity')) || 1;
    items = [{ id, quantity, properties: opts.properties, preview: opts.preview, selling_plan: opts.selling_plan }];
  }
  if (items.some((i) => !i.id)) {
    return Promise.resolve({ ok: false, message: 'ValeraneCart.add : identifiant de variante manquant' });
  }
  if (opts.preview && items.length === 1) items[0].preview = opts.preview;

  const target = eventTarget(opts.source);
  const deferred = CartLinesUpdateEvent.createPromise();
  const refresh = () => getCart().catch(() => null);

  // 1) Affichage optimiste. On construit d'abord la ligne tant que le tiroir est ferme (aucun calcul de mise
  //    en page), puis l'evenement ouvre le tiroir : une seule mise en page, avec l'article deja present.
  performance.mark('vc:add:in');
  let deltaCount = 0;
  try {
    deltaCount = optimisticAdd(items, opts.source);
  } catch (e) {
    console.warn('[ValeraneCart] affichage optimiste', e);
  }
  const opened = openNow(opts.source);
  const event = new CartLinesUpdateEvent({
    action: 'add',
    context: opts.context || 'product',
    lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
    promise: deferred.promise,
  });
  performance.mark('vc:add:built');
  // Tout ce qui n'est pas visible dans le tiroir (pastille de l'en-tete, evenement lines-update et ses ecouteurs :
  // analytics, ventes croisees...) part APRES le premier rendu, pour que le clic ne paie que l'ouverture + la ligne.
  let afterRender = () => {
    afterRender = () => {};
    bumpCount(deltaCount);
    target.dispatchEvent(event);
  };
  if (opened) {
    // apres la glissiere (theme-drawer:open est emis par le tiroir une fois la glissiere finie), au plus tard 1,2 s
    drawerEl()?.addEventListener('theme-drawer:open', () => requestAnimationFrame(() => setTimeout(() => afterRender(), 0)), { once: true });
    setTimeout(() => afterRender(), 1200);
  }
  else afterRender();

  // 2) Requete reseau, en file.
  return enqueue(async ({ isLast, signal }) => {
    try {
      afterRender();
      const sections = sectionIds();
      const itemCount = items.reduce((n, i) => n + i.quantity, 0);
      /** @type {RequestInit} */
      let init;
      if (opts.formData instanceof FormData && items.length === 1) {
        const body = new FormData();
        for (const [k, v] of opts.formData.entries()) body.append(k, /** @type {any} */ (v));
        if (sections.length) {
          body.set('sections', sections.join(','));
          body.set('sections_url', location.pathname);
        }
        init = { method: 'POST', body, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
      } else {
        const payload = {
          items: items.map((i) => ({
            id: Number(i.id),
            quantity: i.quantity,
            ...(i.properties && Object.keys(i.properties).length ? { properties: i.properties } : {}),
            ...(i.selling_plan ? { selling_plan: i.selling_plan } : {}),
          })),
          ...(sections.length ? { sections: sections.join(','), sections_url: location.pathname } : {}),
        };
        init = {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        };
      }
      const res = await fetch(routes().cart_add_url || '/cart/add.js', { ...init, signal });
      const data = await res.json();

      if (data.status || data.errors || !res.ok) throw Object.assign(new Error(data.description || data.message || 'Add to cart failed'), { data });

      // Cles reelles des lignes ajoutees (pour les modifications faites avant la reconciliation).
      const added = Array.isArray(data.items) ? data.items : [data];
      items.forEach((it, i) => {
        const real = added[Math.min(i, added.length - 1)]?.key;
        if (it.__pendingKey && real) state.keyMap.set(it.__pendingKey, real);
      });

      const last = isLast();
      const totalQuantity = parseItemCount(data.sections?.[sections[0]]) ?? state.count ?? itemCount;
      if (last) state.count = totalQuantity;
      deferred.resolve({
        cart: { totalQuantity, lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })) },
        detail: {
          source: SOURCE,
          sourceId: opts.sourceId,
          itemCount,
          productId: opts.productId,
          sections: last ? data.sections : undefined,
          skipRender: !last, // reponse intermediaire : la prochaine remplacera l'affichage
          didError: false,
        },
      });
      if (last) syncFromServer();
      return { ok: true, response: data };
    } catch (error) {
      if (error?.name === 'AbortError') {
        deferred.reject(error);
        return { ok: false, message: 'aborted' };
      }
      const data = error?.data;
      const message = data?.description || data?.message || error?.message || 'Add to cart failed';
      const network = !data;
      target.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: network ? 'SERVICE_UNAVAILABLE' : 'INVALID',
          detail: { description: data?.description, errors: data?.errors },
        })
      );
      showError(message);
      // Retour a l'etat reel : on relit le panier, l'evenement didError re-rend le tiroir depuis le serveur.
      const cart = await refresh();
      if (cart) {
        state.count = cart.item_count;
        state.total = cart.total_price;
      }
      deferred.resolve({
        cart: { totalQuantity: cart?.item_count ?? state.count ?? 0, lines: [] },
        detail: { didError: true, items: cart?.items, source: SOURCE, sourceId: opts.sourceId, itemCount: 0 },
      });
      if (cart && cart.item_count === 0) setTimeout(endSkeleton, 0);
      return { ok: false, message, errors: data?.errors, response: data };
    }
  });
}

/* ------------------------------------------------------------------ API : change / remove */

/** @param {{ key?: string, line?: number, quantity: number, source?: Element }} opts */
function change(opts) {
  const all = rows();
  const row = opts.key ? all.find((r) => r.dataset.key === opts.key) : all[(opts.line ?? 0) - 1];
  const key = opts.key ?? row?.dataset.key;
  const quantity = Math.max(0, Number(opts.quantity) || 0);
  if (!key) return Promise.resolve({ ok: false, message: 'ValeraneCart.change : ligne introuvable' });

  // Quantite deja prise en compte dans l'affichage (et non la valeur du champ : le selecteur +/- l'a deja changee au clic).
  const input0 = /** @type {HTMLInputElement | null} */ (row?.querySelector('input[name="updates[]"]'));
  const oldQty = Number(row?.dataset.vcQty ?? input0?.defaultValue) || 0;
  // Clics rapides : seule la derniere valeur demandee pour cette ligne part au serveur.
  const seq = (state.changeSeq.get(key) || 0) + 1;
  state.changeSeq.set(key, seq);
  let unit = Number(row?.dataset.unitPrice || 0);
  // Ligne rendue par le serveur (pas de prix unitaire memorise) : on le retrouve depuis le prix de ligne affiche,
  // sinon la suppression ne baisserait le total qu'au retour du serveur (changement visible apres coup).
  if (row && !unit && oldQty > 0) {
    const shown = row.querySelector('.cart-items__price-current:not(.cart-items__price-free)')?.textContent ?? '';
    const lineCents = Number(shown.replace(/\D/g, '')) || 0;
    if (lineCents) unit = Math.round(lineCents / oldQty);
  }
  if (row && quantity > 0) {
    setRowQuantity(row, quantity, unit || undefined);
    if (unit) bumpTotal(unit * (quantity - oldQty));
  } else if (row && unit) {
    bumpTotal(-unit * oldQty);
  }
  bumpCount(quantity - oldQty);

  const deferred = CartLinesUpdateEvent.createPromise();
  const target = eventTarget(opts.source ?? document.body);
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: key, quantity }],
      promise: deferred.promise,
    })
  );

  return enqueue(async ({ isLast, signal }) => {
    try {
      if (state.changeSeq.get(key) !== seq) {
        // Un clic plus recent sur la meme ligne est deja en file : sa requete portera la valeur finale.
        deferred.resolve({ cart: { totalQuantity: state.count ?? 0, lines: [] }, detail: { skipRender: true, source: SOURCE, didError: false } });
        return { ok: true, superseded: true };
      }
      const sections = sectionIds();
      const res = await fetch(routes().cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: resolveKey(key),
          quantity,
          ...(sections.length ? { sections: sections.join(','), sections_url: location.pathname } : {}),
        }),
        signal,
      });
      const data = await res.json();
      if (data.errors || data.status) throw Object.assign(new Error(typeof data.errors === 'string' ? data.errors : data.description || 'Cart update failed'), { data });

      const last = isLast();
      const itemCount = parseItemCount(data.sections?.[sections[0]]) ?? data.item_count ?? 0;
      if (last) {
        state.count = data.item_count ?? itemCount;
        state.total = data.total_price ?? state.total;
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: last ? data.sections : undefined,
          skipRender: !last,
          items: data.items,
          itemCount,
          source: SOURCE,
          didError: false,
        },
      });
      return { ok: true, response: data };
    } catch (error) {
      if (error?.name === 'AbortError') {
        deferred.reject(error);
        return { ok: false, message: 'aborted' };
      }
      const data = error?.data;
      const message = (typeof data?.errors === 'string' && data.errors) || data?.description || error?.message || 'Cart update failed';
      // Message dans la ligne (comportement d'origine) + rollback depuis le serveur.
      if (row?.isConnected) {
        const text = row.querySelector('.cart-item__error-text');
        if (text) {
          text.textContent = message;
          row.querySelector('.cart-items__error')?.classList.remove('hidden');
        }
      }
      showError(message);
      target.dispatchEvent(
        new CartErrorEvent({ error: message, code: data ? 'INVALID' : 'SERVICE_UNAVAILABLE' })
      );
      const cart = await getCart().catch(() => null);
      if (cart) {
        // Retour a la valeur vraie tout de suite (quantite, prix de ligne, total, pastille, palier), le rendu serveur confirme ensuite.
        const real = cart.items.find((/** @type {any} */ it) => it.key === resolveKey(key));
        if (row?.isConnected && real) setRowQuantity(row, real.quantity, Math.round(real.final_line_price / real.quantity) || undefined);
        replaceMoneyText(money(state.total ?? 0, 'total'), money(cart.total_price, 'total'));
        state.count = cart.item_count;
        state.total = cart.total_price;
        cartIcon()?.renderCartBubble?.(cart.item_count, false);
        updateTieredOffer(cart.item_count);
        updateFreeShipping(cart.total_price);
      }
      deferred.resolve({
        cart: { totalQuantity: cart?.item_count ?? state.count ?? 0, lines: [] },
        detail: { didError: true, items: cart?.items, source: SOURCE, itemCount: 0 },
      });
      return { ok: false, message };
    }
  });
}

/* ------------------------------------------------------------------ prechargement */

function preloadFromElement(el) {
  const holder = el.closest?.('product-form-component, product-card, quick-add-component, [data-product-url]');
  if (!holder) return;
  const url =
    holder.getAttribute('data-product-url') ||
    holder.querySelector?.('a[href*="/products/"]')?.getAttribute('href');
  if (url) preload(url);
}

function init() {
  // Produit de la page courante et produits survoles / touches (cartes, ajout rapide).
  document.querySelectorAll('product-form-component[data-product-url], lc-product[data-product-url]').forEach((el) => preload(el.getAttribute('data-product-url') || ''));
  const hint = (e) => e.target instanceof Element && preloadFromElement(e.target);
  document.addEventListener('pointerover', hint, { passive: true });
  document.addEventListener('touchstart', hint, { passive: true });
  document.addEventListener('focusin', hint);
  // Etat initial du panier (compteur / total) pour le calcul optimiste.
  idle(() => {
    syncFromServer();
    warmSkeleton();
    warmSpare();
  });
  // Apres chaque reconciliation du tiroir (le HTML serveur retire la ligne de rechange), on la reconstruit au calme.
  const drawer = drawerEl();
  if (drawer) {
    let timer = 0;
    new MutationObserver(() => {
      checkSkeleton();
      clearTimeout(timer);
      timer = window.setTimeout(() => idle(() => (warmSkeleton(), warmSpare())), 400);
    }).observe(drawer, { childList: true, subtree: true });
    new MutationObserver(checkSkeleton).observe(drawer, { attributes: true, attributeFilter: ['class'], subtree: true });
  }
  document.addEventListener('theme-drawer:open', () => state.total == null && syncFromServer());
}

/**
 * Le tag Google (gtag) et les pixels ecoutent « submit » sur le document et y passent ~100 ms de JavaScript
 * synchrone : on ne les laisse pas retarder le premier rendu du tiroir. Au clic, on arrete la propagation, puis
 * apres la glissiere (theme-drawer:open, secours 1,2 s) on redeclenche sur le formulaire un « submit » synthetique
 * (bubbles/cancelable) marque `vcReplay` : les ecouteurs du theme l'ignorent, les tags tiers le voient. Un submit
 * synthetique n'envoie jamais le formulaire. Retourne true si l'evenement est un rejeu (a ignorer par l'appelant).
 * @param {Event} event
 * @returns {boolean}
 */
function deferSubmit(event) {
  if (/** @type {any} */ (event).vcReplay) return true;
  event.stopPropagation();
  const form = event.target instanceof Element ? event.target : null;
  if (!form) return false;
  let done = false;
  const replay = () => {
    if (done) return;
    done = true;
    const again = new Event('submit', { bubbles: true, cancelable: true });
    /** @type {any} */ (again).vcReplay = true;
    /** @type {any} */ (again).submitter = /** @type {any} */ (event).submitter ?? null;
    form.dispatchEvent(again);
  };
  const drawer = drawerEl();
  if (drawer && drawer.hasAttribute('fast-open')) {
    drawer.addEventListener('theme-drawer:open', () => requestAnimationFrame(() => setTimeout(replay, 0)), { once: true });
    setTimeout(replay, 1200);
  } else {
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(replay, 0)));
  }
  return false;
}

const api = {
  version: '1.0.0',
  deferSubmit,
  add,
  change,
  remove: (keyOrLine) => change(typeof keyOrLine === 'number' ? { line: keyOrLine, quantity: 0 } : { key: keyOrLine, quantity: 0 }),
  open: openDrawer,
  close: closeDrawer,
  preload,
  isSkeletonActive: () => state.skActive,
  registerPreview,
  hasPreview: (variantId) => Boolean(state.previews.get(String(variantId))?.title),
  getCart,
  enqueue,
  resolveKey,
  renumber,
  sync: syncFromServer,
  /** Annule les requetes de relecture en cours (ex. navigation). */
  abortPending() {
    state.controller.abort();
    state.controller = new AbortController();
  },
};

if (!(/** @type {any} */ (window).ValeraneCart)) {
  /** @type {any} */ (window).ValeraneCart = api;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.dispatchEvent(new CustomEvent('valerane-cart:ready'));
}

export default api;
